import { type QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Navigate } from "react-router-dom";
import {
  createCollection,
  deleteCollection,
  listBooks,
  listCuratedCollections,
  setCollectionBooks,
  updateCollection,
  type CuratedBook,
  type CuratedCollection,
} from "../api";
import BackLink from "../components/BackLink";
import ConfirmDelete from "../components/ConfirmDelete";
import MutationError from "../components/MutationError";
import Placeholder, { Pending } from "../components/Placeholder";
import { useT } from "../i18n";
import { refetch } from "../refetch";
import { useSession } from "../session";

const inputClass =
  "w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm focus:border-sage focus:outline-none";
const buttonClass =
  "rounded-full bg-sage-deep px-4 py-1.5 text-sm font-medium text-white disabled:opacity-50";
const pillClass =
  "rounded-full border border-line px-3 py-1 text-xs font-medium hover:border-sage disabled:opacity-40";

/**
 * AdminCollections is where a platform admin curates the collections Browse
 * shows above its grid (requirement 0119, ADR-0185): make one, name and
 * describe it, and add, remove and order its books. Every change of order is
 * one write of the whole list, so two edits cannot interleave.
 */
export default function AdminCollections() {
  const { me, isPending } = useSession();
  const t = useT();
  const client = useQueryClient();
  const isAdmin = !!me?.user.platform_admin;
  const list = useQuery({
    queryKey: ["admin", "collections"],
    queryFn: listCuratedCollections,
    enabled: isAdmin,
  });
  const [name, setName] = useState("");
  const create = useMutation({
    mutationFn: () => createCollection(name.trim(), ""),
    onSuccess: () => {
      setName("");
      return refetchCollections(client);
    },
  });

  if (!isPending && !isAdmin) return <Navigate to="/" replace />;
  const items = list.data?.items ?? [];

  return (
    <main className="mx-auto max-w-3xl px-4 py-10">
      <BackLink to="/admin">{t.admin.back}</BackLink>
      <header className="mt-4 mb-6">
        <h1 className="text-[1.75rem] leading-tight font-semibold tracking-tight">
          {t.collections.title}
        </h1>
        <p className="mt-1 text-sm text-muted">{t.collections.what}</p>
      </header>

      <form
        className="mb-8 flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim()) create.mutate();
        }}
      >
        <input
          dir="auto"
          aria-label={t.collections.name}
          placeholder={t.collections.name}
          data-testid="collection-new-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className={inputClass}
        />
        <button
          type="submit"
          data-testid="collection-create"
          disabled={!name.trim() || create.isPending}
          className={`${buttonClass} shrink-0`}
        >
          {t.collections.create}
        </button>
      </form>
      <MutationError error={create.error} fallback={t.common.somethingWrong} />

      {(isPending || list.isPending) && (
        <Pending label={t.common.loading}>
          <Placeholder className="h-40 rounded-card" />
        </Pending>
      )}
      {list.isError && (
        <p role="alert" className="text-sm text-red-600 dark:text-red-400">
          {t.collections.listFailed}{" "}
          <button type="button" onClick={() => void list.refetch()} className="underline">
            {t.common.tryAgain}
          </button>
        </p>
      )}
      {list.data && items.length === 0 && (
        <p data-testid="collections-empty" className="text-sm text-muted">
          {t.collections.empty}
        </p>
      )}

      <ul className="space-y-6">
        {items.map((c) => (
          <CollectionEditor key={c.id} collection={c} />
        ))}
      </ul>
    </main>
  );
}

/** Reads the lists again after a write (`refetch` says why it cancels first). */
function refetchCollections(client: QueryClient) {
  return refetch(client, ["admin", "collections"], ["collections"]);
}

function CollectionEditor({ collection: c }: { collection: CuratedCollection }) {
  const t = useT();
  const client = useQueryClient();
  const [name, setName] = useState(c.name);
  const [description, setDescription] = useState(c.description);
  const refresh = () => refetchCollections(client);
  const save = useMutation({
    mutationFn: () => updateCollection(c.id, { name: name.trim(), description }),
    onSuccess: refresh,
  });
  const remove = useMutation({ mutationFn: () => deleteCollection(c.id), onSuccess: refresh });
  // A series numbers its books as volumes, and a book is in one at most: the
  // server refuses a change that would put one in two, and says so.
  const kind = useMutation({
    mutationFn: (k: "collection" | "series") => updateCollection(c.id, { kind: k }),
    onSuccess: refresh,
  });
  const order = useMutation({
    mutationFn: (ids: string[]) => setCollectionBooks(c.id, ids),
    onSuccess: refresh,
  });
  const ids = c.books.map((b) => b.id);
  const move = (from: number, to: number) => {
    const next = [...ids];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved!);
    order.mutate(next);
  };
  const changed = name.trim() !== c.name || description !== c.description;

  return (
    <li
      data-testid="collection-editor"
      data-name={c.name}
      className="rounded-card border border-line bg-surface p-4"
    >
      <div className="space-y-2">
        <input
          dir="auto"
          aria-label={t.collections.name}
          data-testid="collection-name-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className={`${inputClass} font-semibold`}
        />
        <textarea
          dir="auto"
          aria-label={t.collections.description}
          placeholder={t.collections.description}
          data-testid="collection-description-input"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          className={inputClass}
        />
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            data-testid="collection-save"
            disabled={!changed || !name.trim() || save.isPending}
            onClick={() => save.mutate()}
            className={buttonClass}
          >
            {t.collections.save}
          </button>
          <ConfirmDelete
            label={t.collections.delete}
            question={t.collections.deleteQuestion(c.name)}
            onConfirm={() => remove.mutate()}
            testId="collection-delete"
          />
        </div>
        <div role="radiogroup" aria-label={t.collections.kind} className="flex gap-1.5">
          {(["collection", "series"] as const).map((k) => (
            <button
              key={k}
              type="button"
              role="radio"
              aria-checked={c.kind === k}
              data-testid={`collection-kind-${k}`}
              disabled={kind.isPending || c.kind === k}
              onClick={() => kind.mutate(k)}
              className={`${pillClass} ${c.kind === k ? "border-sage bg-sage/10 text-sage-deep disabled:opacity-100" : ""}`}
            >
              {k === "series" ? t.collections.kindSeries : t.collections.kindCollection}
            </button>
          ))}
        </div>
        <MutationError
          error={save.error ?? remove.error ?? kind.error}
          fallback={t.common.somethingWrong}
        />
      </div>

      <ol data-testid="collection-entries" className="mt-4 space-y-1.5">
        {c.books.length === 0 && <p className="text-sm text-muted">{t.collections.noBooks}</p>}
        {c.books.map((b, i) => (
          <li
            key={b.id}
            data-testid="collection-entry"
            className="flex items-center gap-2 rounded-lg bg-sunk px-3 py-1.5 text-sm"
          >
            <span className="w-5 text-xs text-muted tabular-nums">{i + 1}</span>
            <span dir="auto" className="min-w-0 flex-1 truncate">
              {b.title}
            </span>
            <BookStatus book={b} />
            <button
              type="button"
              aria-label={t.collections.moveUp}
              title={t.collections.moveUp}
              data-testid="collection-entry-up"
              disabled={i === 0 || order.isPending}
              onClick={() => move(i, i - 1)}
              className={pillClass}
            >
              ↑
            </button>
            <button
              type="button"
              aria-label={t.collections.moveDown}
              title={t.collections.moveDown}
              data-testid="collection-entry-down"
              disabled={i === c.books.length - 1 || order.isPending}
              onClick={() => move(i, i + 1)}
              className={pillClass}
            >
              ↓
            </button>
            <button
              type="button"
              data-testid="collection-entry-remove"
              disabled={order.isPending}
              onClick={() => order.mutate(ids.filter((id) => id !== b.id))}
              className={pillClass}
            >
              {t.collections.remove}
            </button>
          </li>
        ))}
      </ol>
      <MutationError error={order.error} fallback={t.common.somethingWrong} />
      <AddBook
        taken={ids}
        disabled={order.isPending}
        onAdd={(bookID) => order.mutate([...ids, bookID])}
      />
    </li>
  );
}

/** Why a reader might not see a linked book: unpublished, private or shared. */
function BookStatus({ book }: { book: CuratedBook }) {
  const t = useT();
  if (!book.published) return <span className="text-xs text-muted">{t.collections.draft}</span>;
  if (book.visibility !== "public")
    return <span className="text-xs text-muted">{t.collections.notPublic(book.visibility)}</span>;
  return null;
}

// A book link pasted in — /books/<id> or the bare id — names a book the
// catalogue does not list: a private or shared one (ADR-0185 lets a
// collection hold any library's books).
const idPattern = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

/**
 * AddBook finds a book by title in the catalogue, as pills to tap — never a
 * dropdown on a phone — or takes a pasted link to one it does not list.
 */
function AddBook({
  taken,
  disabled,
  onAdd,
}: {
  taken: string[];
  disabled: boolean;
  onAdd: (bookID: string) => void;
}) {
  const t = useT();
  const [query, setQuery] = useState("");
  const catalogue = useQuery({ queryKey: ["books", "public"], queryFn: () => listBooks() });
  const q = query.trim().toLocaleLowerCase();
  const pasted = query.match(idPattern)?.[0]?.toLowerCase();
  const matches = q
    ? (catalogue.data?.items ?? [])
        .filter((b) => !taken.includes(b.id) && b.title.toLocaleLowerCase().includes(q))
        .slice(0, 8)
    : [];
  const add = (id: string) => {
    onAdd(id);
    setQuery("");
  };

  return (
    <div className="mt-3">
      <input
        dir="auto"
        aria-label={t.collections.addBook}
        placeholder={t.collections.findBook}
        data-testid="collection-find-book"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        className={inputClass}
      />
      <div className="mt-2 flex flex-wrap gap-1.5">
        {pasted && !taken.includes(pasted) && (
          <button
            type="button"
            data-testid="collection-add-pasted"
            disabled={disabled}
            onClick={() => add(pasted)}
            className={pillClass}
          >
            {t.collections.addThis}
          </button>
        )}
        {!pasted &&
          matches.map((b) => (
            <button
              key={b.id}
              type="button"
              dir="auto"
              data-testid="collection-add-book"
              disabled={disabled}
              onClick={() => add(b.id)}
              className={pillClass}
            >
              {b.title}
            </button>
          ))}
      </div>
    </div>
  );
}
